import { useState, useEffect } from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  HomeOutlined as HomeIcon,
  AddOutlined as PlusIcon,
  ChatOutlined as MessageSquareIcon,
  SettingsOutlined as SettingsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '@client/store';
import { GetUserUUID } from '../..';
import { PyrenzModal, PyrenzModalContent, PyrenzBlueButton } from '@client/theme';

type SetShowLoginModal = (show: boolean) => void;

const settingsTabs = ['account', 'profile', 'persona', 'api', 'customization'] as const;

export function MobileNav({ setShowLoginModal }: { setShowLoginModal: SetShowLoginModal }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user_uuid = useUserStore((state) => state.userUUID);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        await GetUserUUID();
        setIsUserLoggedIn(true);
      } catch {
        setIsUserLoggedIn(false);
      }
    };
    checkAuth();
  }, [user_uuid]);

  const menuItems = [
    { name: t('navigation.home'), icon: <HomeIcon fontSize="small" />, path: '/Home' },
    { name: t('navigation.create'), icon: <PlusIcon fontSize="small" />, path: '/Create' },
    { name: t('navigation.chats'), icon: <MessageSquareIcon fontSize="small" />, path: '/Archive' },
    { name: t('navigation.settings'), icon: <SettingsIcon fontSize="small" />, path: '/Settings' },
  ];

  const handleNavigation = (item: { name: string; path: string }) => {
    const protectedRoutes = [t('navigation.settings'), t('navigation.create'), t('navigation.chats')];

    if (protectedRoutes.includes(item.name) && !isUserLoggedIn) {
      setShowLoginModal(true);
    } else {
      if (item.name === t('navigation.settings')) {
        setSettingsModalOpen(true);
      } else {
        navigate(item.path);
      }
    }
  };

  const handleSelectTab = (tab: (typeof settingsTabs)[number]) => {
    navigate(`/Settings?tab=${tab}`);
    setSettingsModalOpen(false);
  };

  return (
    <>
      <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 50 }} elevation={3}>
        <BottomNavigation showLabels>
          {menuItems.map((item) => (
            <BottomNavigationAction
              key={item.name}
              label={item.name}
              icon={item.icon}
              onClick={() => handleNavigation(item)}
              sx={{
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'scale(1.05)' },
              }}
            />
          ))}
        </BottomNavigation>
      </Paper>

      <PyrenzModal open={settingsModalOpen} onClose={() => setSettingsModalOpen(false)}>
        <PyrenzModalContent
          sx={{
            width: '90vw',
            maxWidth: 400,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('navigation.settings')}
          </Typography>
          {settingsTabs.map((tab) => (
            <PyrenzBlueButton
              key={tab}
              onClick={() => handleSelectTab(tab)}
              sx={{
                width: '100%',
                textTransform: 'capitalize',
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </PyrenzBlueButton>
          ))}
        </PyrenzModalContent>
      </PyrenzModal>
    </>
  );
}
